/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/bulkEmailProducts.scss';

import React, {ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {ActivateHandle} from "../../../../util/interfaces";
import EmailStep from "../../../../models/emailStepModel";
import {CloseButton, WithBackground} from "@dvrd/dvr-controls";
import {BolmateButton, BolmateInput} from "../../../controls";
import {sendGaEvent} from "../../../../util/analytics";
import {onPopupClosed, requestOpenPopup} from "../../../../util/popupUtil";

interface Props {
    onChangeProducts: (selected: Array<string>) => void;
}

function sanitizeEAN(ean: string): string {
    return ean.replace(/\D/g, '').trim();
}

const allowedKeys = [
    'enter', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'home', 'end', 'backspace', 'delete', ' ',
];

function eanTextToList(text: string): Array<string> {
    const splitted = text.split(/\s+/);
    const eans: Set<string> = new Set(splitted.map((ean: string) => sanitizeEAN(ean)).filter((ean: string) => ean.length > 0));
    return Array.from(eans);
}

function EmailProducts(props: Props, ref: ForwardedRef<ActivateHandle<EmailStep>>) {
    const [eans, setEANs] = useState<string>('');
    const [active, setActive] = useState(false);
    const popupId = useRef<string | null>(null);

    function onActivate(emailStep: EmailStep) {
        popupId.current = requestOpenPopup(() => {
            activate(emailStep)
        }, true);
    }

    function activate(emailStep: EmailStep) {
        const {productEans} = emailStep;
        if (!productEans.includes('all') && !productEans.includes('remaining'))
            onChangeEANs(emailStep.productEans.join('\n'));
        setActive(true);
    }

    function onClose() {
        setActive(false);
        if (popupId.current) onPopupClosed(popupId.current);
    }

    function onChangeEANs(value: string) {
        setEANs(value);
    }

    function onKeyDown(evt: React.KeyboardEvent) {
        const {key, metaKey} = evt;
        if (metaKey) return;
        if (allowedKeys.includes(key.toLowerCase()) || /\d/.test(key)) return;
        evt.preventDefault();
    }

    function onSubmit() {
        let productEANs = eanTextToList(eans);
        if (!productEANs.length) productEANs = ['all'];
        sendGaEvent('E-mails', 'Bulk link products', productEANs.length.toString());
        props.onChangeProducts(productEANs);
        onClose();
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    useEffect(() => {
        return function () {
            if (popupId.current) onPopupClosed(popupId.current);
        }
    }, []);

    return (
        <WithBackground onClose={onClose} active={active}>
            <div className='bulk-email-products default-popup'>
                <div className='header-container'>
                    <label className='header-title'>Producten koppelen</label>
                    <CloseButton onClick={onClose}/>
                </div>
                <div className='body-container'>
                    <p className='description'>Vul hieronder de EAN nummers in van de producten die aan de e-mail
                        gekoppeld moeten worden. Scheidt meerdere EAN's met een spatie of enter.</p>
                    <BolmateInput onChange={onChangeEANs} value={eans} label={'EAN\'s'} inputProps={{rows: 20}}
                                  className='eans-field' onKeyDown={onKeyDown} area autoFocus/>
                </div>
                <div className='footer-container btn-2'>
                    <BolmateButton onClick={onSubmit} label='Koppelen'/>
                    <BolmateButton onClick={onClose} label='Sluiten' secondary/>
                </div>
            </div>
        </WithBackground>
    )
}

export default forwardRef<ActivateHandle, Props>(EmailProducts);